import { Button, Col, Form, InputNumber, message, Row, Select } from "antd"
import { useCallback, useEffect, useState } from "react"
import helper from "../utils/helper";
import { OutPutResult, Spinner } from '@operations/shared';
import { getOneCompanyApi, updateDepartmentMaxAdvancePercentApi } from "../utils/apisConsts";
import { errorMessage } from "../utils/commonStringsConsts";

function UpdateDepartmentMaxAdvancePercent() {
    const { Option } = Select
    const [form] = Form.useForm();
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>()
    const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
    const [companies, setCompanies] = useState([])
    const [departments, setDepartments] = useState<any[]>([])
    const [isError, setIsError] = useState(false)
    const [isLoadingUpdateMaxAdvance, setIsLoadingUpdateMaxAdvance] = useState(false)

    const getCompaniesData = useCallback(async () => {
        setIsLoadingCompanies(true)
        setIsError(false)
        const companiesRes: any = await helper.getAllCompanies(true)
        if (companiesRes?.status === 200) {
            setCompanies(companiesRes?.data)
            setIsLoadingCompanies(false)
        } else {
            setIsError(true)
            setIsLoadingCompanies(false)
        }
    }, [])

    useEffect(() => {
        form.resetFields()
        setSelectedDepartmentId(0)
        setIsError(false)
        getCompaniesData()
    }, [getCompaniesData, form])

    const getDepartmentsByCompanyId = async (id: number) => {
        try {
            const resData: any = await helper.callApiRequest(`${getOneCompanyApi}/${id}`, "GET")
            if (resData?.data?.status === 200) {
                setDepartments(resData?.data?.data?.departments)
            } else {
                message.error(resData?.data?.message)
            }
        }
        catch (error: any) {
            message.error(errorMessage)
        }
    }

    const handleSelectCompany = async (id: number) => {
        form.setFieldsValue({ companyDepartmentID: undefined })
        setSelectedDepartmentId(0);
        if (id) await getDepartmentsByCompanyId(id)
        else {
            setDepartments([])
            form.setFieldsValue({ maxAdvancePercentAllowed: undefined })
        }
    }
    const handleSelectDepartment = async (id: number) => {
        setSelectedDepartmentId(id);
        const selectedDepartment = departments?.find((dept: any) => dept?.id === id);
        form.setFieldsValue({ 
            maxAdvancePercentAllowed: selectedDepartment?.maxAdvancePercentAllowed,
            probationPeriod: selectedDepartment?.probationPeriod
        })
    }

    const refreshPage = () => {
        window.location.reload();
    }

    const handleSubmit = async (values: any) => {
        const { companyID, ...rest } = values
        setIsLoadingUpdateMaxAdvance(true)
        try {
            const response: any = await helper.callApiRequest(updateDepartmentMaxAdvancePercentApi, "PATCH", "", rest)
            setIsLoadingUpdateMaxAdvance(false)
            if (response?.data?.status === 200) {
                message.success("Update department maximum advance percentage successfully")
                form.resetFields()
                setSelectedDepartmentId(0)
            } else message.error(response?.data?.message)
        } catch (error: any) {
            setIsLoadingUpdateMaxAdvance(false)
            if (error?.data?.status === 500) message.error(errorMessage)
            else message.error(error?.data?.message)
        }
    }

    return (
        <div>
            {isLoadingCompanies ? <Spinner size='large' /> : isError ? <OutPutResult status={'500'} title={'Oops!'} subTitle={'Something went wrong, please refresh and try again'}
                btnTitle={'Refresh the page'} btnClick={refreshPage} /> :
                <Form
                    layout='vertical'
                    form={form}
                    autoComplete="off"
                    onFinish={handleSubmit}
                >
                    <Row>
                        <Col span={24}>
                            <label>Company<span className="ml-1">*</span></label>
                            <Form.Item name="companyID" rules={[{ required: true, message: 'Required' }]} style={{ width: '100%', margin: '.25rem 0 .5rem' }}>
                                <Select
                                    placeholder='Please select the company'
                                    showSearch
                                    allowClear
                                    onChange={handleSelectCompany}
                                    filterOption={(input, option: any) => option?.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0}
                                >
                                    {companies?.map((company: any) => {
                                        return <Option value={company?.id} key={company?.id}>{company?.name}</Option>
                                    })}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <label>Department<span className="ml-1">*</span></label>
                            <Form.Item name="companyDepartmentID" rules={[{ required: true, message: 'Required' }]} style={{ width: '100%', margin: '.25rem 0 .5rem' }}>
                                <Select
                                    placeholder='Please select the department'
                                    showSearch
                                    allowClear
                                    disabled={!form.getFieldValue('companyID')}
                                    onChange={handleSelectDepartment}
                                    filterOption={(input, option: any) => option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                >
                                    {departments?.map((department: any) => {
                                        return <Option value={department?.id} key={department?.id}>{department?.Department?.nameEn}</Option>
                                    })}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    {selectedDepartmentId && form.getFieldValue('companyID') ?
                        <>
                            <Row>
                                <Col span={24}>
                                    <label htmlFor="">Department Maximum Advance Percentage Allowed</label>
                                    <Form.Item
                                        name="maxAdvancePercentAllowed"
                                        rules={[
                                            {
                                                validator: (_, value) => {
                                                    if (typeof value === 'number' && !Number.isInteger(value)) {
                                                        return Promise.reject('Department maximum advance percentage must be an integer');
                                                    }
                                                    if (typeof value === 'number' && value < 0) {
                                                        return Promise.reject('Department maximum advance percentage must be a positive number');
                                                    }
                                                    else if (typeof value === 'number' && value > 300) {
                                                        return Promise.reject('Department maximum advance percentage cannot be more than 300%');
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            type="number"
                                            placeholder="Enter the department maximum advance percentage allowed"
                                            style={{ width: '100%', margin: '.25rem 0 .5rem' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row>
                                <Col span={24}>
                                    <label htmlFor="">Probation Period</label>
                                    <Form.Item
                                        name="probationPeriod"
                                        rules={[
                                            {
                                                validator: (_, value) => {
                                                    if (typeof value === 'number' && !Number.isInteger(value)) {
                                                        return Promise.reject('Probation Period must be an integer');
                                                    }
                                                    if (typeof value === 'number' && value < 0) {
                                                        return Promise.reject('Probation Period must be a positive number');
                                                    }
                                                    return Promise.resolve();
                                                }
                                            }
                                        ]}
                                    >
                                        <InputNumber
                                            type="number"
                                            placeholder="Enter the Probation Period"
                                            style={{ width: '100%', margin: '.25rem 0 .5rem' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row className="justify-end">
                                <Col span={8}>
                                    <Form.Item className="text-right" style={{ marginBottom: 0 }}>
                                        <Button type="primary" htmlType="submit" loading={isLoadingUpdateMaxAdvance} style={{ marginTop: '.5rem' }}>
                                            Submit
                                        </Button>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                        :
                    null}
                </Form >
            }
        </div >
    )
}

export default UpdateDepartmentMaxAdvancePercent